import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Users, FileText, Briefcase, Package, Calendar, MessageSquare, Flag, UserCheck, UserX, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const StatCard = ({ title, value, icon: Icon, color = 'primary', sub }) => (
  <Card className="card-default" data-testid={`stat-${title.toLowerCase().replace(/\s/g,'-')}`}>
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs sm:text-sm text-muted-foreground">{title}</p>
          <p className="text-2xl sm:text-3xl font-serif font-bold mt-1">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <div className={`h-10 w-10 sm:h-12 sm:w-12 rounded-lg bg-${color}/10 flex items-center justify-center`}>
          <Icon className={`h-5 w-5 sm:h-6 sm:w-6 text-${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

const AnalyticsPage = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/analytics/overview`, { headers: getAuthHeaders(), withCredentials: true });
      setData(res.data);
    } catch { toast.error('Failed to load analytics'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return (
    <div className="min-h-screen bg-background page-with-bottom-nav">
      <Navbar />
      <div className="flex justify-center items-center pt-40"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
    </div>
  );

  if (!data) return null;

  const roles = data.role_breakdown || {};

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="analytics-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <h1 className="font-serif text-3xl font-bold mb-2">Platform Analytics</h1>
        <p className="text-muted-foreground mb-8">Overview of community activity and growth</p>

        {/* Key Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard title="Total Users" value={data.total_users} icon={Users} sub={`${data.pending_users} pending`} />
          <StatCard title="Total Posts" value={data.total_posts} icon={FileText} sub={`${data.posts_this_week} this week`} />
          <StatCard title="Services" value={data.total_services} icon={Briefcase} />
          <StatCard title="Purchases" value={data.total_purchases} icon={Package} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard title="Groups" value={data.total_groups} icon={Users} />
          <StatCard title="Discussions" value={data.total_discussions} icon={MessageSquare} />
          <StatCard title="Meetings" value={data.total_meetings} icon={Calendar} />
          <StatCard title="Pending Reports" value={data.pending_reports} icon={Flag} />
        </div>

        {/* Role Breakdown */}
        <Card className="card-default mb-8">
          <CardHeader><CardTitle className="font-serif text-lg">User Breakdown</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(roles).map(([role, count]) => (
                <div key={role} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    {role === 'admin' ? <UserCheck className="h-4 w-4 text-primary" /> : <UserX className="h-4 w-4 text-primary" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium capitalize">{role}s</p>
                    <p className="text-lg font-bold">{count}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Daily Activity */}
        <Card className="card-default">
          <CardHeader><CardTitle className="font-serif text-lg">Recent Activity (Posts per Day)</CardTitle></CardHeader>
          <CardContent>
            {Object.keys(data.daily_posts || {}).length === 0 ? (
              <p className="text-muted-foreground text-sm py-4 text-center">No recent activity data</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(data.daily_posts).sort(([a], [b]) => b.localeCompare(a)).slice(0, 14).map(([day, count]) => {
                  const maxCount = Math.max(...Object.values(data.daily_posts));
                  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={day} className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground w-20 flex-shrink-0">{day}</span>
                      <div className="flex-1 h-6 bg-muted/30 rounded-full overflow-hidden">
                        <div className="h-full bg-primary/30 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-medium w-8 text-right">{count}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AnalyticsPage;
